import {
  BRITISH_ENGLISH_AUDIO_NAMESPACE,
  CANONICAL_ENGLISH_AUDIO_NAMESPACE,
  normalizeEnglishAccent,
  type EnglishAccent,
} from "./audioAccent";

export interface AudioRefCandidateOptions {
  englishAccent?: EnglishAccent | string | null;
  allowEnglishAccentFallback?: boolean;
  allowLegacyLanguageFallback?: boolean;
}

export function normalizeAudioRef(audioRef: string): string {
  return String(audioRef || "").trim().replace(/^\/+/, "");
}

export function stripLanguagePrefix(audioRef: string): string {
  const normalized = normalizeAudioRef(audioRef);
  const parts = normalized.split("/").filter((part) => part.length > 0);

  if (parts.length < 4 || parts[0] !== "audio") {
    return normalized;
  }

  if (/^(a1|a2|b1|b2)$/i.test(parts[1])) {
    return ["audio", "en", ...parts.slice(1)].join("/");
  }

  const maybeLanguage = parts[1];
  if (!/^[a-z]{2}(?:_[a-z]{2})?$/i.test(maybeLanguage)) {
    return normalized;
  }

  if (parts.length < 5 || !/^(a1|a2|b1|b2)$/i.test(parts[2])) {
    return normalized;
  }

  return ["audio", "en", ...parts.slice(2)].join("/");
}

function addUniqueCandidate(candidates: string[], audioRef: string): void {
  const normalized = normalizeAudioRef(audioRef);
  if (normalized && !candidates.includes(normalized)) {
    candidates.push(normalized);
  }
}

function toCanonicalEnglishAudioRef(audioRef: string): string {
  const normalized = normalizeAudioRef(audioRef);
  const parts = normalized.split("/").filter(Boolean);
  if (parts.length < 3 || parts[0] !== "audio") {
    return normalized;
  }

  if (/^(a1|a2|b1|b2)$/i.test(parts[1])) {
    return [CANONICAL_ENGLISH_AUDIO_NAMESPACE, ...parts.slice(1)].join("/");
  }

  if (
    (parts[1] === "en" || parts[1] === "en_gb" || parts[1] === "en_us")
    && parts.length >= 4
    && /^(a1|a2|b1|b2)$/i.test(parts[2])
  ) {
    return [CANONICAL_ENGLISH_AUDIO_NAMESPACE, ...parts.slice(2)].join("/");
  }

  return normalized;
}

export function buildEnglishAccentAudioRef(
  audioRef: string,
  englishAccent?: EnglishAccent | string | null,
): string {
  const canonical = toCanonicalEnglishAudioRef(audioRef);
  const parts = canonical.split("/").filter(Boolean);
  if (
    parts.length < 5
    || parts[0] !== "audio"
    || parts[1] !== "en"
    || !/^(a1|a2|b1|b2)$/i.test(parts[2])
  ) {
    return canonical;
  }

  return normalizeEnglishAccent(englishAccent) === "uk"
    ? [BRITISH_ENGLISH_AUDIO_NAMESPACE, ...parts.slice(2)].join("/")
    : canonical;
}

export function buildAudioRefCandidates(
  audioRef: string,
  options: AudioRefCandidateOptions = {},
): string[] {
  const normalized = normalizeAudioRef(audioRef);
  if (!normalized) {
    return [];
  }

  const candidates: string[] = [];
  if (normalizeEnglishAccent(options.englishAccent) === "uk") {
    addUniqueCandidate(candidates, buildEnglishAccentAudioRef(normalized, "uk"));
    if (options.allowEnglishAccentFallback === false) {
      return candidates;
    }
  }
  addUniqueCandidate(candidates, normalized);
  addUniqueCandidate(candidates, buildEnglishAccentAudioRef(normalized, "us"));

  if (options.allowLegacyLanguageFallback === true) {
    const fallback = stripLanguagePrefix(normalized);
    addUniqueCandidate(candidates, fallback);
  }
  return candidates;
}

export function parseAudioRefScope(audioRef: string): {
  targetLanguage: string;
  level: string;
} | null {
  const normalized = normalizeAudioRef(audioRef);
  const parts = normalized.split("/").filter(Boolean);
  if (parts.length < 4 || parts[0] !== "audio") {
    return null;
  }
  if (/^(a1|a2|b1|b2)$/i.test(parts[1])) {
    return {
      targetLanguage: "en",
      level: parts[1].toLowerCase(),
    };
  }
  if (parts.length < 5 || !/^(a1|a2|b1|b2)$/i.test(parts[2])) {
    return null;
  }
  return {
    targetLanguage: parts[1].toLowerCase(),
    level: parts[2].toLowerCase(),
  };
}
